/**
 * NotiCardWrap: 알림 카드 팝업 컴포넌트
 * wehago-conference RenewalComponents/SVContent/NotiCardWrap 에서 TS 리팩토링
 *
 * 카드 종류:
 * - REQUEST_INVITE: 회의 참석 요청 (마스터 전용)
 * - REQUEST_FLOOR: 발언권 요청 (마스터 전용)
 * - UPDATE_CHATTING: 새 채팅 메시지
 */
import React, { useState, useMemo, useCallback } from 'react';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { notificationActions, type NotiCard, type NotiCardType } from '@/store/slices/notificationSlice';
import { EMPTY_PROFILE, toAbsoluteProfileUrl } from '@/utils/profileUtils';
import { translateText } from '@/i18n';

// ─── 개별 카드 타입 Props ────────────────────────────────────────────────────

interface NotiCardWrapProps {
  onResponseJoinRequest?: (userId: string, status: 'allow' | 'deny') => void;
  onGrantFloorTarget?: (userId: string, type: 'accept' | 'reject') => void;
  onOpenSideChat?: () => void;
}

// ─── 참석 요청 카드 ──────────────────────────────────────────────────────────

interface InviteCardProps {
  cards: NotiCard[];
  onResponse: (userId: string, status: 'allow' | 'deny') => void;
  onRemove: (id: string) => void;
}

const InviteCard: React.FC<InviteCardProps> = ({ cards, onResponse, onRemove }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (cards.length === 0) return null;

  if (cards.length === 1) {
    const card = cards[0];
    return (
      <div className="rtcNewPopover typeRequest rtcCustoms_notiCard">
        <div className="rtcNewPopover__request">
          <strong className="rtcNewPopover__requestText">
            {translateText('{{name}}님이 참석을 요청하였습니다.', { name: card.userName })}
          </strong>
          <div className="btnBox">
            <button
              type="button"
              className="rtcNewPopover__requestBtn"
              onClick={() => { onResponse(card.userId, 'deny'); onRemove(card.id); }}
            >
              {translateText('거절')}
            </button>
            <button
              type="button"
              className="rtcNewPopover__requestBtn blue"
              onClick={() => { onResponse(card.userId, 'allow'); onRemove(card.id); }}
            >
              {translateText('승인')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // 복수 요청
  return (
    <div className="rtcNewPopover typeRequest rtcCustoms_notiCard--multi">
      <div className="rtcNewPopover__request v2">
        <div className="rtcNewPopover__requestTop">
          <strong className="rtcNewPopover__requestText">
            {translateText('회의 참여 요청이 있어요!')}
          </strong>
          <div className="btnBox">
            <button
              type="button"
              className="rtcNewPopover__requestBtn"
              onClick={() => cards.forEach((c) => { onResponse(c.userId, 'deny'); onRemove(c.id); })}
            >
              {translateText('거절')}
            </button>
            <button
              type="button"
              className="rtcNewPopover__requestBtn blue"
              onClick={() => cards.forEach((c) => { onResponse(c.userId, 'allow'); onRemove(c.id); })}
            >
              {translateText('전체 승인')}
            </button>
          </div>
        </div>
        <div className="rtcNewPopover__requestList scrollCustom">
          <ul>
            {cards.map((card, idx) => {
              if (!isExpanded && idx > 1) return null;
              return (
                <li key={card.id}>
                  <p className="rtcNewPopover__requestListText ellipsis">{card.userName}</p>
                  <div className="btnBox">
                    <button
                      type="button"
                      className="rtcNewPopover__requestBtn"
                      onClick={() => { onResponse(card.userId, 'deny'); onRemove(card.id); }}
                    >
                      {translateText('거절')}
                    </button>
                    <button
                      type="button"
                      className="rtcNewPopover__requestBtn blue2"
                      onClick={() => { onResponse(card.userId, 'allow'); onRemove(card.id); }}
                    >
                      {translateText('승인')}
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
        {cards.length > 2 && (
          <div className="rtcNewPopover__requestMore">
            <button
              type="button"
              className={`rtcNewPopover__requestMoreBtn${isExpanded ? ' open' : ''}`}
              onClick={() => setIsExpanded((v) => !v)}
            >
              {translateText(isExpanded ? '닫기' : '더보기')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── 발언권 요청 카드 ────────────────────────────────────────────────────────

interface FloorCardProps {
  cards: NotiCard[];
  onGrant: (userId: string, type: 'accept' | 'reject') => void;
  onRemove: (id: string) => void;
  onRemoveByType: (type: NotiCardType) => void;
}

const FloorCard: React.FC<FloorCardProps> = ({ cards, onGrant, onRemove, onRemoveByType }) => {
  if (cards.length === 0) return null;

  const first = cards[0];
  const isMultiple = cards.length > 3;

  return (
    <div className="rtcNewPopover typeFloor rtcCustoms_notiCard">
      <div className="rtcNewPopover__request">
        <strong className="rtcNewPopover__requestText">
          {isMultiple ? (
            <>{translateText('{{name}}님 외 {{count}}명', { name: first.userName, count: cards.length - 1 })}</>
          ) : (
            <span>{first.userName}<span className="toastWrap__iconHand" /></span>
          )}
        </strong>
        <p className="rtcNewPopover__requestDesc">{translateText('발언권을 요청하였습니다.')}</p>
        <div className="btnBox">
          {isMultiple ? (
            <button
              type="button"
              className="rtcNewPopover__requestBtn blue"
              onClick={() => onRemoveByType('REQUEST_FLOOR')}
            >
              {translateText('확인')}
            </button>
          ) : (
            <>
              <button
                type="button"
                className="rtcNewPopover__requestBtn refuse"
                onClick={() => { onGrant(first.userId, 'reject'); onRemove(first.id); }}
              >
                {translateText('거절')}
              </button>
              <button
                type="button"
                className="rtcNewPopover__requestBtn accept"
                onClick={() => { onGrant(first.userId, 'accept'); onRemove(first.id); }}
              >
                {translateText('승인')}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── 채팅 알림 카드 ──────────────────────────────────────────────────────────

interface ChatCardProps {
  cards: NotiCard[];
  onOpen: () => void;
}

const ChatCard: React.FC<ChatCardProps> = ({ cards, onOpen }) => {
  if (cards.length === 0) return null;

  const last = cards[cards.length - 1];
  const profileUrl = toAbsoluteProfileUrl(last.profileUrl) || EMPTY_PROFILE;

  return (
    <div className="rtcNewPopover typeChat rtcCustoms_notiCard--chat" onClick={onOpen}>
      <div className="rtcNewPopover__request">
        <div className="rtcNewPopover__chatProfile">
          <img src={profileUrl} alt="" />
        </div>
        <div className="rtcNewPopover__chatContent">
          <strong className="rtcNewPopover__chatName">{last.userName}</strong>
          <p className="rtcNewPopover__chatText ellipsis">{last.text}</p>
        </div>
        {cards.length > 1 && (
          <span className="rtcNewPopover__chatCount">{cards.length}</span>
        )}
      </div>
    </div>
  );
};

// ─── NotiCardWrap 메인 ───────────────────────────────────────────────────────

const NotiCardWrap: React.FC<NotiCardWrapProps> = ({
  onResponseJoinRequest,
  onGrantFloorTarget,
  onOpenSideChat,
}) => {
  const dispatch = useAppDispatch();
  const cards = useAppSelector((s) => s.notification.cards);

  const grouped = useMemo(() => {
    const result: Record<NotiCardType, NotiCard[]> = {
      REQUEST_INVITE: [],
      REQUEST_FLOOR: [],
      UPDATE_CHATTING: [],
    };
    for (const card of cards) {
      result[card.type]?.push(card);
    }
    return result;
  }, [cards]);

  const handleRemove = useCallback((id: string) => {
    dispatch(notificationActions.removeCard(id));
  }, [dispatch]);

  const handleRemoveByType = useCallback((type: NotiCardType) => {
    dispatch(notificationActions.removeCardsByType(type));
  }, [dispatch]);

  const handleResponseJoin = useCallback((userId: string, status: 'allow' | 'deny') => {
    onResponseJoinRequest?.(userId, status);
  }, [onResponseJoinRequest]);

  const handleGrantFloor = useCallback((userId: string, type: 'accept' | 'reject') => {
    onGrantFloorTarget?.(userId, type);
  }, [onGrantFloorTarget]);

  const handleOpenChat = useCallback(() => {
    onOpenSideChat?.();
    dispatch(notificationActions.removeCardsByType('UPDATE_CHATTING'));
  }, [dispatch, onOpenSideChat]);

  if (cards.length === 0) return null;

  return (
    <>
      <FloorCard
        cards={grouped.REQUEST_FLOOR}
        onGrant={handleGrantFloor}
        onRemove={handleRemove}
        onRemoveByType={handleRemoveByType}
      />
      <InviteCard
        cards={grouped.REQUEST_INVITE}
        onResponse={handleResponseJoin}
        onRemove={handleRemove}
      />
      <ChatCard
        cards={grouped.UPDATE_CHATTING}
        onOpen={handleOpenChat}
      />
    </>
  );
};

export default NotiCardWrap;
